import React, { useState } from 'react';
import { Mail, Phone, X, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_BASE_URL = "https://ercs.kooturavu.tn.gov.in/api/external";
const API_KEY = "ercs_l1G3HxPovullLXK0ge36XgtR4D33wDU9";

const LoginPortal = () => {
  const { language } = useLanguage();
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [step, setStep] = useState('username');
  const [selectedPortal, setSelectedPortal] = useState('');
  const [showPortalPopup, setShowPortalPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginResponse, setLoginResponse] = useState(null);

  const content = {
    en: {
      title: "RCS Portals",
      subtitle: "Tamil Nadu Cooperative Societies",
      caption: "Login using registered Mobile Number or Email ID to access the portals.",
      userId: "Email / Mobile Number",
      otp: "Enter OTP",
      userIdPlaceholder: "Enter email or mobile number",
      otpPlaceholder: "Enter 4-digit OTP",
      requestOtp: "Request OTP",
      verifyOtp: "Verify OTP",
      processing: "Processing...",
      selectPortal: "Select Portal",
      footer: "© 2024 Tamil Nadu Cooperative Societies. All rights reserved.",
      errorLabel: "Error: ",
      successLabel: "Success: ",
      redirecting: "Redirecting to",
      otpSent: "OTP has been sent to your registered",
      email: "email",
      mobile: "mobile number",
      backToLogin: "Back to Login",
      resendOtp: "Resend OTP"
    },
    ta: {
      title: "RCS போர்ட்டல்கள்",
      subtitle: "தமிழ்நாடு கூட்டுறவு சங்கங்கள்",
      caption: "போர்ட்டல்களை அணுக பதிவுசெய்யப்பட்ட மொபைல் எண் அல்லது மின்னஞ்சல் ஐடியைப் பயன்படுத்தி உள்நுழையவும்.",
      userId: "மின்னஞ்சல் / மொபைல் எண்",
      otp: "OTP ஐ உள்ளிடவும்",
      userIdPlaceholder: "மின்னஞ்சல் அல்லது மொபைல் எண்ணை உள்ளிடவும்",
      otpPlaceholder: "4-இலக்க OTP ஐ உள்ளிடவும்",
      requestOtp: "OTP கோரவும்",
      verifyOtp: "OTP சரிபார்க்கவும்",
      processing: "செயல்படுத்துகிறது...",
      selectPortal: "போர்ட்டலைத் தேர்ந்தெடுக்கவும்",
      footer: "© 2024 தமிழ்நாடு கூட்டுறவு சங்கங்கள். அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை.",
      errorLabel: "பிழை: ",
      successLabel: "வெற்றி: ",
      redirecting: "திசைதிருப்புகிறது",
      otpSent: "OTP உங்கள் பதிவுசெய்யப்பட்ட",
      email: "மின்னஞ்சலுக்கு",
      mobile: "மொபைல் எண்ணுக்கு",
      backToLogin: "உள்நுழைவுக்குத் திரும்பு",
      resendOtp: "OTP ஐ மீண்டும் அனுப்பவும்"
    }
  };

  const t = content[language];

  const portals = [
    {
      id: 'ercs',
      name: 'eRCS',
      key: 'ercs_portal',
      color: 'bg-blue-50'
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      url: 'https://dashboard.kooturavu.tn.gov.in/auth/sign-in',
      color: 'bg-green-50'
    },
    {
      id: 'list',
      name: 'LIST',
      key: 'kooturavu_ai',
      url: 'https://rcs.venba.app/verify',
      color: 'bg-yellow-50'
    }
  ];

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!username.trim()) {
      setError("Please enter email or mobile number");
      return;
    }

    setIsLoading(true);

    try {
      const isEmail = username.includes("@");
      const requestBody = {
        ...(isEmail ? { email: username } : { contact_no: username })
      };

      const response = await fetch(`${API_BASE_URL}/request-otp`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log("🔍 Request OTP Response:", result);

      if (response.ok && result.status && result.data?.session_id) {
        setSessionId(result.data.session_id);
        setSuccess(`${t.otpSent} ${isEmail ? t.email : t.mobile}`);
        setStep('otp');
      } else {
        setError(result.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("❌ Request OTP error:", error);
      setError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp.trim()) {
      setError("Please enter OTP");
      return;
    }

    if (otp.length !== 4) {
      setError("OTP must be 4 digits");
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = {
        session_id: sessionId,
        otp: otp
      };

      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: "POST",
        headers: {
          "X-API-Key": API_KEY,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();
      console.log("🔍 Verify OTP Response:", result);

      if (response.ok && result.status && result.data?.access_token) {
        setLoginResponse(result);
        setShowPortalPopup(true);
      } else {
        setError(result.message || "Invalid OTP");
      }
    } catch (error) {
      console.error("❌ Verify OTP error:", error);
      setError("OTP verification failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePortalSelect = async (portalId) => {
    setSelectedPortal(portalId);
    setIsLoading(true);

    try {
      const result = loginResponse;
      const token = result.data.access_token;
      const portal = portals.find(p => p.id === portalId);

      setSuccess(`${t.redirecting} ${portal?.name}...`);
if (portalId === 'dashboard') {
  // Extract Court Case Dashboard SSO token to pass to dashboard
  // so Sidenav can use it for the Court Case button
  let courtCaseToken = '';
  const courtCaseUrl = result.data?.sso_urls?.court_case_dashboard;
  if (courtCaseUrl) {
    const urlParams = new URLSearchParams(courtCaseUrl.split('?')[1]);
    courtCaseToken = urlParams.get('token') || '';
  }

  const dashboardUrl =
    `${portal.url}?token=${token}` +
    `&refresh_token=${result.data.refresh_token}` +
    `&court_case_token=${courtCaseToken}`;

  setTimeout(() => {
    window.location.href = dashboardUrl;
  }, 1500);
}
      else if (portalId === 'erp') {
        const erpUrl = `${portal.url}?token=${token}`;
        setTimeout(() => {
          window.location.href = erpUrl;
        }, 1500);
      }
      else if (portalId === 'ercs' && result.data?.sso_urls?.ercs_portal) {
        const ssoUrl = result.data.sso_urls.ercs_portal;
        console.log("📋 eRCS SSO URL from API:", ssoUrl);
        setTimeout(() => {
          window.location.href = ssoUrl;
        }, 1500);
      }
      else if (portalId === 'list' && result.data?.sso_urls?.kooturavu_ai) {
        const ssoUrl = result.data.sso_urls.kooturavu_ai;
        console.log("📋 LIST SSO URL from API:", ssoUrl);

        const urlParams = new URLSearchParams(ssoUrl.split('?')[1]);
        const ssoToken = urlParams.get('token');
        console.log("🔑 Extracted Token:", ssoToken);

        if (ssoToken) {
          const finalUrl = `${portal.url}?token=${ssoToken}`;
          console.log("🚀 Redirecting to LIST:", finalUrl);
          setTimeout(() => {
            window.location.href = finalUrl;
          }, 1500);
        } else {
          setError("Token not found in SSO response");
          setIsLoading(false);
        }
      }
      else if (result.data?.sso_urls && portal?.key) {
        const ssoUrl = result.data.sso_urls[portal.key];
        console.log(`📋 ${portal.name} SSO URL from API:`, ssoUrl);

        if (ssoUrl) {
          setTimeout(() => {
            window.location.href = ssoUrl;
          }, 1500);
        } else {
          setError("SSO URL not available for this portal");
          setIsLoading(false);
        }
      } else {
        setError("SSO configuration not found");
        setIsLoading(false);
      }

    } catch (error) {
      console.error("❌ Redirect error:", error);
      setError("Redirect failed. Please try again.");
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep('username');
    setOtp('');
    setSessionId('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-start justify-center p-4 pt-8">
      <div className="w-full max-w-md bg-white rounded-xl shadow-xl">

        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <h2 className="text-2xl font-bold text-gray-800 text-center">{t.title}</h2>
          <p className="text-gray-500 text-center text-sm mt-1">{t.subtitle}</p>
          <p className="text-gray-600 text-center text-sm mt-3 leading-relaxed">{t.caption}</p>
        </div>

        {/* Alert Messages */}
        {(error || success) && (
          <div className="mx-6 mt-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start">
                <svg className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span><strong className="font-semibold">{t.errorLabel}</strong>{error}</span>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-start">
                <svg className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span><strong className="font-semibold">{t.successLabel}</strong>{success}</span>
              </div>
            )}
          </div>
        )}

        {/* Login Form */}
        <div className="px-6 pt-8 pb-2">
          {step === 'username' ? (
            <form onSubmit={handleRequestOtp} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">
                  {t.userId}
                </label>
                <div className="relative">
                  {username.includes("@") ? (
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  ) : (
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  )}
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.replace(/[\s\t]/g, ""))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white"
                    placeholder={t.userIdPlaceholder}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? t.processing : t.requestOtp}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">
                  {t.otp}
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm bg-white text-center text-2xl tracking-widest font-semibold"
                    placeholder="----"
                    required
                    disabled={isLoading}
                    maxLength={4}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors text-sm disabled:bg-blue-400 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? t.processing : t.verifyOtp}
              </button>

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="flex items-center text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                  disabled={isLoading}
                >
                  <ArrowLeft size={16} className="mr-1" />
                  {t.backToLogin}
                </button>

                <button
                  type="button"
                  onClick={handleRequestOtp}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                  disabled={isLoading}
                >
                  {t.resendOtp}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 rounded-b-xl px-4 py-3">
          <p className="text-center text-xs text-gray-500">
            {t.footer}
          </p>
        </div>
      </div>

      {/* Portal Selection Popup */}
      {showPortalPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">{t.selectPortal}</h3>
              <button
                onClick={() => setShowPortalPopup(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {success && (
              <div className="mx-6 mt-4">
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-start">
                  <svg className="w-5 h-5 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span><strong className="font-semibold">{t.successLabel}</strong>{success}</span>
                </div>
              </div>
            )}

            <div className="p-6 space-y-3">
              {portals.map((portal) => (
                <button
                  key={portal.id}
                  onClick={() => handlePortalSelect(portal.id)}
                  className={`w-full p-4 ${portal.color} border-2 border-gray-200 rounded-lg hover:shadow-md hover:border-blue-400 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed`}
                  disabled={isLoading}
                >
                  <h4 className="font-bold text-gray-800 text-lg">{portal.name}</h4>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPortal;